/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { getFseConsent, setFseConsent, revokeFseConsent } from '@/lib/fseLombardia';

const formatDT = (iso) => { try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso || ''; } };

export const ConsentCard = ({ onChange }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setState(await getFseConsent()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore caricamento consenso'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doToggle = async (value) => {
    setBusy(true);
    try {
      if (value) {
        const res = await setFseConsent(true);
        toast.success('Consenso FSE registrato');
        setState((prev) => ({ ...(prev || {}), consent: res }));
      } else {
        await revokeFseConsent();
        toast.success('Consenso revocato');
        await load();
      }
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Operazione fallita');
    } finally { setBusy(false); }
  };

  if (loading) return <Skeleton className="h-52 rounded-2xl" />;

  const granted = !!state?.consent?.granted;
  const form = state?.form || {};

  return (
    <Card data-testid="fse-consent-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {granted
            ? <ShieldCheck className="h-5 w-5 text-emerald-600" />
            : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
          <CardTitle className="font-display">Consenso FSE Lombardia</CardTitle>
          <Badge variant={granted ? 'success' : 'secondary'} className="ml-auto">
            {granted ? 'Attivo' : 'Non attivo'}
          </Badge>
        </div>
        <CardDescription>Versione modulo: {form.version || '—'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm bg-muted/50 rounded-md p-3 leading-relaxed">
          {form.text}
        </div>
        <div className="text-[11px] text-muted-foreground font-mono break-all">
          hash: {form.hash}
        </div>
        {state?.consent && (
          <div className="text-[11px] text-muted-foreground">
            {granted && state.consent.granted_at && <>Concesso il {formatDT(state.consent.granted_at)}</>}
            {!granted && state.consent.revoked_at && <>Revocato il {formatDT(state.consent.revoked_at)}</>}
          </div>
        )}
        <div className="flex items-center gap-3 pt-1">
          <Switch
            checked={granted}
            disabled={busy}
            onCheckedChange={doToggle}
            data-testid="fse-consent-toggle"
          />
          <span className="text-sm font-medium">
            {granted ? 'Consenso attivo — puoi revocarlo in qualsiasi momento' : 'Concedi il consenso per abilitare l\'export FSE'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConsentCard;
